import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DeckForm } from "./DeckForm";
import type { Deck, CreateDeckData, UpdateDeckData } from "@/lib/api/deck-api";
import type { Division } from "@/lib/types/deck-builder";
import type { UnitMap } from "@/lib/types/deck-builder";

interface DeckFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deck?: Deck | null;
  deckId?: number;
  initialDeckCode?: string;
  divisions: Division[];
  unitMap: UnitMap;
  discordUsername: string;
  isVerified: boolean;
  onSubmit: (data: CreateDeckData | UpdateDeckData) => Promise<void>;
}

export function DeckFormModal({
  open,
  onOpenChange,
  deck,
  deckId,
  initialDeckCode,
  divisions,
  unitMap,
  discordUsername,
  isVerified,
  onSubmit,
}: DeckFormModalProps) {
  const [preservedDeckCode, setPreservedDeckCode] = useState<string>("");

  // Preserve deck code when modal is opened with a code
  useEffect(() => {
    if (open && initialDeckCode) {
      setPreservedDeckCode(initialDeckCode);
    }
  }, [open, initialDeckCode]);

  // Use preserved code if available, otherwise use initialDeckCode
  const effectiveDeckCode = preservedDeckCode || initialDeckCode || "";

  const handleSubmit = async (data: CreateDeckData | UpdateDeckData) => {
    try {
      await onSubmit(data);
      // On success, close modal and clear preserved code
      setPreservedDeckCode("");
      onOpenChange(false);
    } catch (error) {
      // On error, keep modal open so user can fix issues
      throw error;
    }
  };

  const handleCancel = () => {
    // Just close the modal without clearing preserved code
    // This allows user to reopen and still have their deck code
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{deck ? "Edit Deck" : "Create New Deck"}</DialogTitle>
        </DialogHeader>
        <DeckForm
          deck={deck}
          deckId={deckId || deck?.id}
          initialDeckCode={effectiveDeckCode}
          divisions={divisions}
          unitMap={unitMap}
          discordUsername={discordUsername}
          isVerified={isVerified}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          showTitle={false}
        />
      </DialogContent>
    </Dialog>
  );
}

